import { cn } from "@/lib/utils";
import type { TierPriceDisplay } from "@/lib/platform-pricing-sale";

type Variant = "marketing" | "billing";

type Props = {
  display: TierPriceDisplay;
  variant?: Variant;
  saleTitle?: string | null;
  className?: string;
};

export function PlanPriceDisplay({ display, variant = "marketing", saleTitle, className }: Props) {
  const { saleActive, listPriceLabel, salePriceLabel, percentOff } = display;

  if (!saleActive || !salePriceLabel || percentOff == null) {
    return (
      <div className={cn("flex items-baseline gap-1", className)}>
        <span
          className={cn(
            "font-bold tracking-tight text-foreground",
            variant === "marketing" ? "text-4xl text-slate-900" : "text-4xl"
          )}
        >
          {listPriceLabel}
        </span>
        <span
          className={cn(
            "font-medium text-muted-foreground",
            variant === "marketing" ? "text-sm text-slate-500" : "text-sm"
          )}
        >
          /month
        </span>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {saleTitle ? (
        <p
          className={cn(
            "inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
            variant === "marketing"
              ? "bg-rose-100 text-rose-800 ring-1 ring-rose-200/80"
              : "bg-rose-500/10 text-rose-700 dark:text-rose-200"
          )}
        >
          {saleTitle}
        </p>
      ) : (
        <p
          className={cn(
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
            variant === "marketing"
              ? "bg-rose-100 text-rose-800 ring-1 ring-rose-200/80"
              : "bg-rose-500/10 text-rose-700 dark:text-rose-200"
          )}
        >
          {percentOff}% off
        </p>
      )}
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        <span
          className={cn(
            "text-lg font-medium line-through decoration-2",
            variant === "marketing" ? "text-slate-400" : "text-muted-foreground"
          )}
        >
          {listPriceLabel}
        </span>
        <div className="flex items-baseline gap-1">
          <span
            className={cn(
              "font-bold tracking-tight",
              variant === "marketing" ? "text-4xl text-slate-900" : "text-4xl text-foreground"
            )}
          >
            {salePriceLabel}
          </span>
          <span
            className={cn(
              "font-medium",
              variant === "marketing" ? "text-sm text-slate-500" : "text-sm text-muted-foreground"
            )}
          >
            /month
          </span>
        </div>
      </div>
      <p className={cn("text-xs font-medium", variant === "marketing" ? "text-emerald-700" : "text-emerald-600")}>
        Save {percentOff}% — was {listPriceLabel}/mo
      </p>
    </div>
  );
}

/** Compact preview card for owner dashboard */
export function PlanPriceSalePreview({
  display,
  saleTitle,
}: {
  display: TierPriceDisplay;
  saleTitle?: string | null;
}) {
  return (
    <div className="rounded-xl border border-border/80 bg-muted/20 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {display.tier.charAt(0).toUpperCase() + display.tier.slice(1)}
      </p>
      <PlanPriceDisplay display={display} variant="billing" saleTitle={saleTitle} className="mt-2" />
    </div>
  );
}
